import React, { useEffect, useState } from 'react';
import { generateSitemap } from '../../utils/sitemapGenerator';

// Cache the sitemap for 1 hour (3600000 ms) to avoid regenerating on every request
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour
let cachedSitemap = null;
let cacheTimestamp = null;

// Function to clear cache (can be called to force regeneration)
export const clearSitemapCache = () => {
  cachedSitemap = null;
  cacheTimestamp = null;
  console.log('🗑️ Sitemap cache cleared - will regenerate on next request');
};

const SitemapXMLRoute = () => {
  const [sitemapContent, setSitemapContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateAndSetSitemap = async () => {
      try {
        // Check if we have a valid cached sitemap
        const now = Date.now();
        const cacheValid = cachedSitemap && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION;
        
        if (cacheValid) {
          console.log('📦 Using cached sitemap (regenerated automatically every hour)');
          setSitemapContent(cachedSitemap);
          setLoading(false);
          return;
        }

        console.log('🚀 Generating fresh sitemap with latest content from API...');
        console.log('🔍 Current URL:', window.location.href);
        console.log('⏰ Timestamp:', new Date().toISOString());
        
        // Add a small delay to ensure any tokens are loaded
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Generate the sitemap with all dynamic content
        const sitemap = await generateSitemap();
        
        // Cache the sitemap
        cachedSitemap = sitemap;
        cacheTimestamp = Date.now();
        
        console.log('✅ Sitemap generated and cached successfully');
        console.log('📊 Sitemap length:', sitemap.length, 'characters');
        console.log('🔢 URL count:', (sitemap.match(/<url>/g) || []).length);
        console.log('⏰ Cache expires at:', new Date(cacheTimestamp + CACHE_DURATION).toLocaleString());
        
        setSitemapContent(sitemap);
        setLoading(false);
        
        // Set the document title and content type
        document.title = 'Sitemap';
        
        // Try to set content type meta tag
        const existingMeta = document.querySelector('meta[http-equiv="Content-Type"]');
        if (existingMeta) {
          existingMeta.content = 'application/xml; charset=utf-8';
        } else {
          const metaTag = document.createElement('meta');
          metaTag.httpEquiv = 'Content-Type';
          metaTag.content = 'application/xml; charset=utf-8';
          document.head.appendChild(metaTag);
        }
        
      } catch (error) {
        console.error('❌ Error generating sitemap:', error);
        console.error('❌ Error details:', error.stack);
        console.error('❌ Error message:', error.message);
        
        // Use cached sitemap even if expired, as fallback
        if (cachedSitemap) {
          console.log('⚠️ Using expired cache as fallback');
          setSitemapContent(cachedSitemap);
          setLoading(false);
        } else {
          // Create a minimal fallback sitemap
          const fallbackSitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>https://www.sabinehospitals.com/</loc>
    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>
  </url>
  <url>
    <loc>https://www.sabinehospitals.com/about</loc>
    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>
  </url>
  <url>
    <loc>https://www.sabinehospitals.com/services</loc>
    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>
  </url>
  <url>
    <loc>https://www.sabinehospitals.com/doctors</loc>
    <lastmod>${new Date().toISOString().split('T')[0]}</lastmod>
  </url>
</urlset>`;
          
          setSitemapContent(fallbackSitemap);
          setLoading(false);
        }
      }
    };

    generateAndSetSitemap();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        fontFamily: 'Arial, sans-serif'
      }}>
        <h2>🔄 Generating Sitemap...</h2>
        <p>Please wait while we fetch all pages from the API...</p>
        <div style={{
          display: 'inline-block',
          width: '20px',
          height: '20px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Return the XML content in a pre-formatted way
  return (
    <div style={{ margin: 0, padding: 0 }}>
      <pre style={{ 
        whiteSpace: 'pre-wrap', 
        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
        fontSize: '13px',
        lineHeight: '1.4',
        margin: 0,
        padding: '10px',
        backgroundColor: '#f8f9fa',
        color: '#333',
        border: 'none',
        overflow: 'auto',
        maxHeight: '100vh'
      }}>
        {sitemapContent}
      </pre>
      
      {/* Add some helpful info at the bottom */}
      <div style={{
        padding: '20px',
        backgroundColor: '#e9ecef',
        borderTop: '1px solid #dee2e6',
        fontFamily: 'Arial, sans-serif',
        fontSize: '14px'
      }}>
        <h3 style={{ margin: '0 0 10px 0', color: '#495057' }}>
          📋 Sitemap Information
        </h3>
        <p style={{ margin: '5px 0', color: '#6c757d' }}>
          <strong>Generated:</strong> {new Date().toLocaleString()}
        </p>
        <p style={{ margin: '5px 0', color: '#6c757d' }}>
          <strong>Total URLs:</strong> {(sitemapContent.match(/<url>/g) || []).length}
        </p>
        <p style={{ margin: '5px 0', color: '#6c757d' }}>
          <strong>Domain:</strong> https://www.sabinehospitals.com
        </p>
        <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#868e96' }}>
          💡 <strong>Tip:</strong> Right-click and "Save As" to download this sitemap, 
          then upload it to your web server root directory via FileZilla.
        </p>
      </div>
    </div>
  );
};

export default SitemapXMLRoute;